<template>
    <main>
        <div class="searchZone">
            <div class="searchBox">
                <NInput v-model:value="query" placeholder="Search" size="large" @input="searchUpdate" />
            </div>
            <div class="infoResults" v-if="searchFailed">Search is unavailable right now.</div>
            <div class="infoResults" v-else-if="totalHits">{{ totalHits }} results</div>

            <div class="results">
                <template v-for="group in results">
                    <div class="group" v-if="group.indexUid == 'eic-articles' && group.hits.length > 0">
                        <h2 class="headerText baron">news</h2>
                        <div class="articles">
                            <template v-for="article in group.hits" :key="article.url">
                                <ItemArticle :article="article" />
                            </template>
                        </div>
                    </div>
                    <div class="group" v-else-if="group.indexUid == 'eic-publications' && group.hits.length > 0">
                        <h2 class="headerText baron">publications</h2>
                        <div class="publications">
                            <template v-for="publication in group.hits" :key="publication.url">
                                <ItemBook :book="publication" v-if="publication.type == 'bookshop'" />
                                <ItemPublication :publication="publication" v-else-if="publication.type == 'position_paper' || publication.type == 'statistic'" />
                                <ItemPublication :publication="publication" page_url="publications-annual-reports-slug" v-else-if="publication.type == 'annual_report_excerpt' || publication.type == 'annual_report'" />
                            </template>
                        </div>
                    </div>
                    <div class="group" v-else-if="group.indexUid == 'eic-conferences' && group.hits.length > 0">
                        <h2 class="headerText baron">conferences</h2>
                        <div class="conferences">
                            <template v-for="conference in group.hits" :key="conference.url">
                                <ItemConference :conference="conference" />
                            </template>
                        </div>
                    </div>
                </template>
            </div>
        </div>

        <WebsiteFooter />
    </main>
</template>

<script lang="ts" setup>
import { NInput } from 'naive-ui'
const router = useRouter()
const route = useRoute()

const query = ref(route.query.q as string)

const { data: payload, refresh: refresResults } = useAsyncData(`search-${query.value}`, async () => {
    const q = query.value || ''
    if (!q) return { results: [], failed: false }

    try {
        const response = await $fetch<{ results?: unknown[] }>('https://cms.eic-federation.eu/search', {
            method: 'POST',
            body: { q },
            timeout: 8000,
        })
        return { results: response?.results || [], failed: false }
    } catch {
        return { results: [], failed: true }
    }
})

const results = computed(() => payload.value?.results || [])
const searchFailed = computed(() => payload.value?.failed === true)

const totalHits = computed(() => {
    if (results.value && results.value.length > 0) {
        let hits = 0
        for (const r of results.value) hits += r.totalHits
        return hits
    } else return null
})

const searchUpdate = useDebounceFn(async () => {
    await refresResults()
    router.replace({ query: { q: query.value } })
})

useSeoMeta({
    title: 'Search | EIC Federation',
    ogTitle: 'Search | EIC Federation',
    twitterTitle: 'Search | EIC Federation'
})
</script>

<style lang="scss" scoped>
main {
    min-height: 100vh;
    display: grid;
    grid-template-rows: 1fr min-content;
}

.searchBox {
    padding-top: var(--size-padding-header);

    &:deep(.n-input) {
        --n-color: white;
        --n-color-hover: white;
        --n-color-focus: white;

        --n-border: none;
        --n-border-hover: none;
        --n-border-focus: none;
        --n-border-radius: 0;

        --n-text-color: var(--color-text);
        --n-text-color-hover: var(--color-primary);
        --n-text-color-focus: var(--color-primary);
        --n-placeholder-color: var(--color-text-light);
        --n-caret-color: var(--color-primary);

        --n-height: 72px;

        font-family: 'HelveticaNowProDisplay', Arial, Helvetica, sans-serif;
        font-weight: bold;
        font-size: var(--text-size-title1);
        line-height: var(--text-line-title1);

        .n-input__state-border {
            display: none;
        }
    }
}

.infoResults {
    padding: var(--size-gutter-double) var(--size-margin-page) var(--size-gutter-large) var(--size-margin-page);
}

.results {
    display: flex;
    flex-direction: column;
    gap: var(--size-gutter-sections);

    .group {
        margin: 0 var(--size-margin-page);

        display: flex;
        flex-direction: column;
        gap: var(--size-gutter-double);

        h2 {
            border-top: var(--size-thickness) solid var(--color-primary);
            font-weight: normal;
            color: var(--color-primary);
            padding-top: var(--size-margin-page);
        }

        .articles,
        .publications {
            display: grid;
            grid-template-columns: repeat(4, minmax(0, 1fr));
            gap: var(--size-margin-page);
        }

        .conferences {
            display: grid;
            grid-template-columns: repeat(5, minmax(0, 1fr));
            gap: var(--size-gutter-double) var(--size-margin-page);
        }
    }
}

@media screen and (max-width: 1280px) {
    .results {
        .group {

            .articles,
            .publications {
                grid-template-columns: repeat(3, minmax(0, 1fr));
            }

            .conferences {
                grid-template-columns: repeat(4, minmax(0, 1fr));
            }
        }
    }
}

@media screen and (max-width: 1024px) {
    .results {
        .group {

            .articles,
            .publications {
                grid-template-columns: repeat(2, minmax(0, 1fr));
            }

            .conferences {
                grid-template-columns: repeat(3, minmax(0, 1fr));
            }
        }
    }
}

@media screen and (max-width: 600px) {
    .results {
        width: 100vw;
        overflow: hidden;

        .group {
            width: 100%;
            overflow: hidden;

            margin-bottom: calc(0px - var(--size-gutter-sections));
            margin-left: 0;

            h2 {
                margin: 0 var(--size-margin-page);
            }

            .articles,
            .publications {
                display: flex;
                flex-direction: row;
                gap: 0;

                overflow-x: auto;
                overflow-y: hidden;
                scroll-snap-type: x mandatory;
                scrollbar-width: none;
                -ms-overflow-style: none;

                scroll-behavior: smooth;
                -webkit-overflow-scrolling: touch;

                &::-webkit-scrollbar {
                    display: none;
                }

                padding-bottom: var(--size-gutter-sections);
                padding-right: 48px;

                &:deep(.itemArticle),
                &:deep(.book),
                &:deep(.publication) {
                    scroll-snap-align: start;
                    scroll-snap-align: start;
                    width: calc(100vw - 48px);
                    flex-shrink: 0;
                    border-left: var(--size-margin-page) solid white;
                }
            }

            .conferences {
                display: flex;
                flex-direction: row;
                gap: 0;

                overflow-x: auto;
                overflow-y: hidden;
                scroll-snap-type: x mandatory;
                scrollbar-width: none;
                -ms-overflow-style: none;

                scroll-behavior: smooth;
                -webkit-overflow-scrolling: touch;

                &::-webkit-scrollbar {
                    display: none;
                }

                padding-bottom: var(--size-gutter-sections);
                padding-right: 100px;

                &:deep(.conference) {
                    scroll-snap-align: start;
                    scroll-snap-align: start;
                    width: calc(100vw - 100px);
                    flex-shrink: 0;
                    border-left: var(--size-margin-page) solid white;
                }
            }
        }
    }
}
</style>